/**
 * Invoice Totals — row-gating derivation
 *
 * Pure decision layer for {@link InvoiceTotals}: which secondary rows a canonical
 * gross-tender {@link InvoiceTotalsData} should surface. Extracted from the component
 * so the branching (and especially the `showAmountDue` invariant) is unit-testable in
 * node env, matching the `deriveKeyBenefits` precedent in this domain.
 *
 * @module butterUI/domains/billing/InvoiceTotals/deriveInvoiceTotalsRows
 */

import { deriveOtherPaid } from '@butter/library/pricing'
import type { InvoiceTotals as InvoiceTotalsData } from '@lgc/database/types/domains/payment'

export interface InvoiceTotalsRows {
  hasDiscount: boolean
  hasTax: boolean
  hasProcessingFee: boolean
  /** A processing-fee adjustment (D63) — the fee was lowered when the invoice was paid. */
  hasAdjustment: boolean
  hasCredit: boolean
  /** Non-credit tenders (card, bank debit, counter) settled against the invoice. */
  hasOtherPayments: boolean
  /**
   * Non-credit money received, in dollars (signed like the invoice). Chosen so
   * `total − creditApplied − otherPaid === amountDue`: the rows beneath Total add up.
   */
  otherPaid: number
  /**
   * Surface an explicit "Balance Due" row only once something has settled — otherwise it
   * just repeats Total. `buildTotalsFromStored` rounds both `total` and `amountDue`
   * (`total - amountPaid`, floored at 0 for a positive invoice), so an unpaid invoice has
   * `amountPaid === 0` and yields exact equality (`amountDue === total`) — the row stays
   * hidden. The floor is also why a REVERSAL must keep its negative `amountDue`: clamping it
   * to 0 would break that equality and surface the row on every reversal.
   */
  showAmountDue: boolean
}

export function deriveInvoiceTotalsRows(totals: InvoiceTotalsData): InvoiceTotalsRows {
  // The formula lives in @butter/library/pricing so the invoice PDF prints the same figure.
  const otherPaid = deriveOtherPaid(totals)
  // `!== 0`, not `> 0`: a reversal (D65) carries every component negative, and a row that
  // vanished would leave the visible rows not adding up to the Total beneath them.
  return {
    hasDiscount: totals.discountTotal !== 0,
    hasTax: totals.tax !== 0,
    hasProcessingFee: totals.processingFee !== 0,
    // `?? 0`: a shipped client (a mobile OTA bundle, a cached web build) can read an API that
    // predates the field.
    hasAdjustment: (totals.adjustmentTotal ?? 0) !== 0,
    hasCredit: totals.creditApplied !== 0,
    hasOtherPayments: otherPaid !== 0,
    otherPaid,
    showAmountDue: totals.amountDue !== totals.total,
  }
}
